/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Add people to an existing MLS group.

import { Search, UserPlus, X } from "lucide-react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	Modal,
	Pressable,
	ScrollView,
	StyleSheet,
	Text,
	TextInput,
	View,
} from "react-native";
import { MAX_GROUP_PEER_ACCOUNTS } from "../conversation/groupConversation";
import { Avatar } from "../components/Avatar";
import type { NewChatModalDirectoryUser } from "./NewChatModal";

export interface AddGroupMembersModalProps {
	readonly open: boolean;
	readonly onDismiss: () => void;
	/** Peer account ids already in the group (excludes local user). */
	readonly existingMemberIds: readonly string[];
	readonly onSearch: (
		query: string,
		signal: AbortSignal,
	) => Promise<readonly NewChatModalDirectoryUser[]>;
	readonly onAdd: (
		members: readonly NewChatModalDirectoryUser[],
	) => Promise<void>;
	/** Max additional peers allowed (defaults to roster headroom). */
	readonly maxAdditionalPeers?: number;
}

export function AddGroupMembersModal(
	props: AddGroupMembersModalProps,
): JSX.Element | null {
	const {
		open,
		onDismiss,
		existingMemberIds,
		onSearch,
		onAdd,
		maxAdditionalPeers = Math.max(
			0,
			MAX_GROUP_PEER_ACCOUNTS - existingMemberIds.length,
		),
	} = props;

	const existingSet = useMemo(
		() => new Set(existingMemberIds),
		[existingMemberIds],
	);

	const [query, setQuery] = useState("");
	const [results, setResults] = useState<readonly NewChatModalDirectoryUser[]>(
		[],
	);
	const [selected, setSelected] = useState<readonly NewChatModalDirectoryUser[]>(
		[],
	);
	const [isLoading, setIsLoading] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const selectedIds = new Set(selected.map((u) => u.id));

	useEffect(() => {
		if (!open) return;
		const controller = new AbortController();
		abortRef.current?.abort();
		abortRef.current = controller;
		const t = setTimeout(async () => {
			setIsLoading(true);
			setError(null);
			try {
				const users = await onSearch(query, controller.signal);
				if (!controller.signal.aborted) {
					setResults(users);
				}
			} catch (err) {
				if (controller.signal.aborted) return;
				const status = (err as { status?: number } | null)?.status;
				const serverMessage = (err as { serverMessage?: string } | null)
					?.serverMessage;
				if (status === 429) {
					setError(
						serverMessage ??
							"You're searching too fast — wait a moment and try again.",
					);
				} else {
					setError(
						serverMessage ?? "Couldn't search the directory. Try again.",
					);
				}
			} finally {
				if (!controller.signal.aborted) setIsLoading(false);
			}
		}, 450);
		return () => {
			controller.abort();
			clearTimeout(t);
		};
	}, [query, open, onSearch]);

	useEffect(() => {
		if (!open) {
			setQuery("");
			setResults([]);
			setSelected([]);
			setError(null);
			setIsAdding(false);
		}
	}, [open]);

	const addMember = (user: NewChatModalDirectoryUser): void => {
		if (existingSet.has(user.id)) return;
		if (selectedIds.has(user.id)) return;
		if (maxAdditionalPeers <= 0) {
			setError(
				`This group already has the maximum of ${MAX_GROUP_PEER_ACCOUNTS} other members.`,
			);
			return;
		}
		if (selected.length >= maxAdditionalPeers) {
			setError(`You can add at most ${maxAdditionalPeers} more people.`);
			return;
		}
		setError(null);
		setSelected((prev) => [...prev, user]);
	};

	const removeMember = (userId: string): void => {
		setSelected((prev) => prev.filter((u) => u.id !== userId));
	};

	const handleAdd = async (): Promise<void> => {
		if (selected.length < 1) {
			setError("Select at least one person to add.");
			return;
		}
		setIsAdding(true);
		setError(null);
		try {
			await onAdd(selected);
			onDismiss();
		} catch (err) {
			const serverMessage = (err as { serverMessage?: string } | null)
				?.serverMessage;
			const message =
				err instanceof Error && err.message.length > 0
					? err.message
					: serverMessage && serverMessage.length > 0
						? serverMessage
						: "Couldn't add those members. Try again.";
			setError(message);
			setIsAdding(false);
		}
	};

	if (!open) return null;

	const canAdd = selected.length >= 1 && !isAdding && maxAdditionalPeers > 0;

	return (
		<Modal
			visible={open}
			transparent
			animationType="none"
			onRequestClose={onDismiss}
		>
			<Pressable
				accessibilityRole="none"
				onPress={onDismiss}
				style={StyleSheet.absoluteFill}
				className="
					z-50 flex-1 items-center justify-start pt-[8vh] px-md
					bg-black/60
				"
			>
				<Pressable
					onPress={() => undefined}
					className="
						w-full max-w-[520px] max-h-[80vh] flex flex-col
						rounded-xl border
						bg-light-surface-primary border-light-border-secondary
						dark:bg-[#1A1D24] dark:border-white/[0.06]
						shadow-2xl
					"
				>
					<View className="flex flex-row items-center justify-between px-lg pt-lg pb-md">
						<Text className="m-0 font-sans font-bold text-headline text-light-text-primary dark:text-white">
							Add people
						</Text>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Close"
							onPress={onDismiss}
							className="
								w-8 h-8 rounded-lg flex items-center justify-center
								hover:bg-light-surface-tertiary
								dark:hover:bg-white/[0.06]
							"
						>
							<X size={16} strokeWidth={2} color="#475467" />
						</Pressable>
					</View>

					<View className="px-lg pb-sm">
						<View
							className="
								flex flex-row items-center
								h-10 px-md gap-sm
								rounded-lg border
								bg-light-surface-secondary dark:bg-white/[0.04]
								border-light-border-secondary dark:border-white/[0.08]
							"
						>
							<Search size={16} strokeWidth={2} color="#888d96" />
							<TextInput
								value={query}
								onChangeText={setQuery}
								placeholder="Search by name, email or phone…"
								autoCapitalize="none"
								autoCorrect={false}
								className="
									flex-1
									text-footnote text-light-text-primary dark:text-white
								"
							/>
						</View>
						{maxAdditionalPeers <= 0 ? (
							<Text className="font-sans text-caption text-light-text-secondary dark:text-white/55 mt-sm m-0">
								This group is full ({MAX_GROUP_PEER_ACCOUNTS} other members max).
							</Text>
						) : null}
					</View>

					{selected.length > 0 ? (
						<View className="px-lg pb-sm flex flex-row flex-wrap gap-xs">
							{selected.map((u) => (
								<MemberChip
									key={u.id}
									user={u}
									onRemove={() => removeMember(u.id)}
									disabled={isAdding}
								/>
							))}
						</View>
					) : null}

					<ScrollView
						accessibilityLiveRegion="polite"
						className="flex-1 min-h-0 px-sm pb-sm"
					>
						{error !== null && (
							<Text className="font-sans text-footnote text-light-accent-rose px-md py-sm m-0">
								{error}
							</Text>
						)}
						{isLoading && results.length === 0 ? (
							<SkeletonRows />
						) : results.length === 0 && !isLoading ? (
							<Text className="font-sans text-footnote text-light-text-secondary dark:text-white/55 text-center px-md py-lg m-0">
								{query.trim().length === 0
									? "Search for people to invite by name, email or phone."
									: "No one matches that search."}
							</Text>
						) : (
							<View className="flex flex-col gap-[2px]">
								{results.map((u) => {
									const inGroup = existingSet.has(u.id);
									const isSelected = selectedIds.has(u.id);
									return (
										<View
											key={u.id}
											className="
												w-full flex flex-row items-center gap-md
												px-md py-sm rounded-lg
											"
										>
											<Avatar
												initials={
													u.displayName.charAt(0).toUpperCase() || "?"
												}
												{...(u.avatarUri !== undefined
													? { imageUri: u.avatarUri }
													: {})}
												size="medium"
											/>
											<View className="flex-1 min-w-0 flex flex-col gap-[2px]">
												<Text
													numberOfLines={1}
													className="font-sans text-footnote font-semibold text-light-text-primary dark:text-white"
												>
													{u.displayName}
												</Text>
												<Text
													numberOfLines={1}
													className="font-sans text-caption text-light-text-secondary dark:text-white/55"
												>
													{[u.role, u.company].filter(Boolean).join(" · ")}
												</Text>
											</View>
											<Pressable
												accessibilityRole="button"
												accessibilityLabel={
													inGroup
														? "Already in group"
														: isSelected
															? "Selected"
															: `Add ${u.displayName}`
												}
												disabled={isAdding || inGroup || isSelected}
												onPress={() => addMember(u)}
												className="
													flex flex-row items-center gap-xs px-sm py-xs rounded-lg
													bg-light-surface-tertiary dark:bg-white/[0.06]
													disabled:opacity-40
												"
											>
												<UserPlus size={14} color="#475467" />
												<Text className="font-sans text-caption font-semibold text-light-text-primary dark:text-white">
													{inGroup
														? "In group"
														: isSelected
															? "Added"
															: "Add"}
												</Text>
											</Pressable>
										</View>
									);
								})}
							</View>
						)}
					</ScrollView>

					<View className="px-lg py-md border-t border-light-border-secondary dark:border-white/[0.06]">
						<Pressable
							accessibilityRole="button"
							disabled={!canAdd}
							onPress={() => void handleAdd()}
							className="
								h-11 rounded-lg flex items-center justify-center
								bg-light-accent-brand dark:bg-dark-accent-brand
								disabled:opacity-40
							"
						>
							<Text className="font-sans text-footnote font-semibold text-white">
								{isAdding
									? "Adding…"
									: `Add to group (${selected.length})`}
							</Text>
						</Pressable>
					</View>
				</Pressable>
			</Pressable>
		</Modal>
	);
}

function MemberChip(props: {
	readonly user: NewChatModalDirectoryUser;
	readonly onRemove: () => void;
	readonly disabled: boolean;
}): JSX.Element {
	const { user, onRemove, disabled } = props;
	return (
		<View
			className="
				flex flex-row items-center gap-xs pl-sm pr-xs py-xs rounded-full
				bg-light-surface-tertiary dark:bg-white/[0.08]
			"
		>
			<Text
				numberOfLines={1}
				className="font-sans text-caption text-light-text-primary dark:text-white max-w-[120px]"
			>
				{user.displayName}
			</Text>
			<Pressable
				accessibilityRole="button"
				accessibilityLabel={`Remove ${user.displayName}`}
				disabled={disabled}
				onPress={onRemove}
				className="w-5 h-5 rounded-full items-center justify-center"
			>
				<X size={12} strokeWidth={2} color="#475467" />
			</Pressable>
		</View>
	);
}

function SkeletonRows(): JSX.Element {
	return (
		<View className="flex flex-col gap-[2px]">
			{Array.from({ length: 4 }).map((_, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton index is fine
					key={i}
					className="flex flex-row items-center gap-md px-md py-sm"
				>
					<View className="w-10 h-10 rounded-full bg-light-surface-tertiary dark:bg-white/[0.06]" />
					<View className="flex-1 flex flex-col gap-xs">
						<View className="h-3 w-2/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
						<View className="h-3 w-3/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
					</View>
				</View>
			))}
		</View>
	);
}
